import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Smartphone, RefreshCw, Loader2, ShieldAlert, Fingerprint, ScanFace, MapPin, Nfc, Clock } from 'lucide-react';
import api from '../services/api';

interface LogRow {
  logID: number;
  employeeID: number;
  fullName: string;
  employeeCode?: string;
  departmentName?: string;
  logDate: string;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  workDuration?: string | null;
  source: string;
  mobileAuthMethod?: string | null;
  mobileLatitude?: number | null;
  mobileLongitude?: number | null;
  mobileLocationAccuracy?: number | null;
  isMockLocation?: boolean;
  isOutsideWorkHours?: boolean;
  proxyByName?: string | null;
}

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysAgoStr = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtTime = (t?: string | null) => {
  if (!t) return '—';
  const d = new Date(t);
  if (isNaN(d.getTime())) return t.substring(11, 16);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
const fmtDuration = (h?: string | null) => {
  if (!h) return '—';
  // TimeSpan tipi "hh:mm:ss" gelir
  const m = String(h).match(/^(\d+):(\d+)/);
  return m ? `${m[1]}s ${m[2]}dk` : String(h);
};

const MobileLogs: React.FC = () => {
  const [start, setStart] = useState(daysAgoStr(7));
  const [end, setEnd] = useState(todayStr());
  const [search, setSearch] = useState('');
  const [onlySuspicious, setOnlySuspicious] = useState(false);
  const [rows, setRows] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r: any = await api.get(`/mobile/logs?startDate=${start}&endDate=${end}`);
      // Backend {success, data:[...]} veya direkt [...] donebilir — hepsini karsıla
      const raw = r?.data ?? r;
      const data: LogRow[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      setRows(data);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Kayıtlar yüklenemedi');
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, []); // ilk yukleme, sonra Uygula butonu ile

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR');
    return rows.filter(r => {
      if (onlySuspicious && !(r.isMockLocation || r.isOutsideWorkHours)) return false;
      if (!q) return true;
      return (
        (r.fullName ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
        (r.employeeCode ?? '').toLocaleLowerCase('tr-TR').includes(q) ||
        (r.departmentName ?? '').toLocaleLowerCase('tr-TR').includes(q)
      );
    });
  }, [rows, search, onlySuspicious]);

  const stats = useMemo(() => ({
    total: rows.length,
    face: rows.filter(r => (r.mobileAuthMethod ?? '').toUpperCase().includes('FACE')).length,
    nfc: rows.filter(r => (r.mobileAuthMethod ?? '').toUpperCase().includes('NFC')).length,
    mock: rows.filter(r => r.isMockLocation).length,
    outside: rows.filter(r => r.isOutsideWorkHours).length,
  }), [rows]);

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800 flex items-center gap-2">
            <Smartphone size={26} className="text-blue-600" />
            Mobil Giriş/Çıkış Kayıtları
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            NexVera PDKS Android uygulamasından yapılan kayıtlar
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Yenile
        </button>
      </div>

      {/* 5 Stat kart */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="TOPLAM" value={stats.total} color="amber" icon={<Smartphone size={16} />} />
        <StatCard label="YÜZ" value={stats.face} color="blue" icon={<ScanFace size={16} />} />
        <StatCard label="NFC" value={stats.nfc} color="purple" icon={<Nfc size={16} />} />
        <StatCard label="ŞÜPHELİ KONUM" value={stats.mock} color="red" icon={<ShieldAlert size={16} />} />
        <StatCard label="SAAT DIŞI" value={stats.outside} color="orange" icon={<Clock size={16} />} />
      </div>

      {/* Filtre */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Başlangıç</label>
          <input type="date" value={start} onChange={e => setStart(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Bitiş</label>
          <input type="date" value={end} onChange={e => setEnd(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Ara</label>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Ad, soyad, kod, departman..."
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none" />
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-60">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Uygula
        </button>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
          <input type="checkbox" checked={onlySuspicious} onChange={e => setOnlySuspicious(e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500" />
          Sadece şüpheli
        </label>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
      )}

      {/* Tablo */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-black text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">Personel</th>
                <th className="px-4 py-3">Tarih</th>
                <th className="px-4 py-3">Giriş</th>
                <th className="px-4 py-3">Çıkış</th>
                <th className="px-4 py-3">Süre</th>
                <th className="px-4 py-3">Yöntem</th>
                <th className="px-4 py-3">Konum</th>
                <th className="px-4 py-3">Uyarı</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center text-slate-400">
                    <Smartphone size={32} className="mx-auto opacity-30 mb-2" />
                    <p className="text-sm">Bu tarih aralığında mobil kayıt bulunamadı</p>
                  </td>
                </tr>
              ) : filtered.map(r => {
                const method = (r.mobileAuthMethod ?? '').toUpperCase();
                const lat = r.mobileLatitude, lng = r.mobileLongitude;
                return (
                  <tr key={r.logID} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-800">{r.fullName}</p>
                      <p className="text-[11px] text-slate-500">#{r.employeeCode ?? '—'} · {r.departmentName ?? '—'}</p>
                      {r.proxyByName && (
                        <p className="text-[10px] text-amber-600 mt-0.5">Yetkili: {r.proxyByName}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      {new Date(r.logDate).toLocaleDateString('tr-TR')}
                    </td>
                    <td className="px-4 py-3 font-mono text-emerald-700">{fmtTime(r.checkInTime)}</td>
                    <td className="px-4 py-3 font-mono text-blue-700">{fmtTime(r.checkOutTime)}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDuration(r.workDuration)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-[11px] font-bold text-slate-700">
                        {method.includes('FACE') ? <><ScanFace size={12} /> YÜZ</>
                          : method.includes('NFC') ? <><Nfc size={12} /> NFC</>
                          : method.includes('QR') ? <><Fingerprint size={12} /> QR</>
                          : method || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {lat != null && lng != null ? (
                        <a
                          href={`https://www.google.com/maps?q=${lat},${lng}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <MapPin size={12} /> Haritada
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {r.isMockLocation && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">
                            <ShieldAlert size={10} /> ŞÜPHELİ KONUM
                          </span>
                        )}
                        {r.isOutsideWorkHours && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px] font-bold">
                            <Clock size={10} /> SAAT DIŞI
                          </span>
                        )}
                        {!r.isMockLocation && !r.isOutsideWorkHours && (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-center text-xs text-slate-400 pt-2">
        Kaynak: <code className="px-1 bg-slate-100 rounded text-slate-600 font-mono">Source='MOBILE'</code> olan AttendanceLogs kayıtları
      </p>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: 'amber' | 'blue' | 'purple' | 'red' | 'orange'; icon: React.ReactNode }> = ({ label, value, color, icon }) => {
  const cls: Record<string, string> = {
    amber: 'border-l-amber-400 bg-amber-50',
    blue: 'border-l-blue-400 bg-blue-50',
    purple: 'border-l-purple-400 bg-purple-50',
    red: 'border-l-red-400 bg-red-50',
    orange: 'border-l-orange-400 bg-orange-50',
  };
  const txt: Record<string, string> = {
    amber: 'text-amber-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    red: 'text-red-700',
    orange: 'text-orange-700',
  };
  return (
    <div className={`border border-slate-200 border-l-4 rounded-xl p-4 ${cls[color]}`}>
      <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-wider ${txt[color]}`}>
        {icon} {label}
      </div>
      <p className="text-2xl font-black text-slate-800 mt-1">{value}</p>
    </div>
  );
};

export default MobileLogs;
